import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility for merging Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format date for display
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Debounce function calls
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Sleep utility for async operations
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Timing-safe string comparison to prevent timing attacks (CWE-208)
 * Always compares all characters regardless of where strings differ
 */
export function timingSafeEqual(a: string, b: string): boolean {
  const maxLength = Math.max(a.length, b.length)
  let result = a.length ^ b.length

  // Always iterate through all characters to prevent timing leaks
  for (let i = 0; i < maxLength; i++) {
    const charA = i < a.length ? a.charCodeAt(i) : 0
    const charB = i < b.length ? b.charCodeAt(i) : 0
    result |= charA ^ charB
  }

  return result === 0
}

/**
 * Validates and sanitizes a redirect path to prevent Open Redirect attacks (CWE-601)
 * Only allows relative paths starting with '/' that don't redirect to external sites
 */
export function safeRedirectPath(path: string | null, fallback: string): string {
  // Return fallback if path is empty or null
  if (!path || typeof path !== 'string') {
    return fallback
  }

  const trimmedPath = path.trim()

  // Must start with a single forward slash (relative path)
  if (!trimmedPath.startsWith('/')) {
    return fallback
  }

  // Reject protocol-relative URLs (//example.com)
  if (trimmedPath.startsWith('//')) {
    return fallback
  }

  // Reject URLs with protocol (http://, https://, javascript:, etc.)
  if (trimmedPath.includes('://') || trimmedPath.toLowerCase().startsWith('javascript:')) {
    return fallback
  }

  // Reject backslash tricks (/\example.com can be interpreted as //example.com in some browsers)
  if (trimmedPath.includes('\\')) {
    return fallback
  }

  // Reject paths with encoded characters that could bypass checks
  // Check for encoded slashes, backslashes, or colons
  const decoded = decodeURIComponent(trimmedPath)
  if (decoded.startsWith('//') || decoded.includes('://') || decoded.includes('\\')) {
    return fallback
  }

  return trimmedPath
}